import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FiArrowLeft, FiArrowRight, FiClock, FiAlertTriangle, FiCheckCircle, FiCalendar } from 'react-icons/fi'
import useBlogTracking, { trackBlogCta } from '../hooks/useBlogTracking'
import NewsletterInline from '../components/NewsletterInline'

const SLUG = 'registro-horario-obligatorio-espana'

const BlogRegistroHorario = () => {
  useBlogTracking({ slug: SLUG, category: 'Normativa Laboral', readMinutes: 10 })
  return (
    <>
      <Helmet>
        <title>Registro Horario Obligatorio en España 2026: Guía Completa para Pymes | PresenzIA</title>
        <meta name="description" content="Guía completa sobre la obligación de registro horario en España (RD-ley 8/2019). Qué empresas deben cumplir, sanciones de hasta 187.515 €, cómo implementarlo y soluciones digitales para pymes." />
        <meta name="keywords" content="registro horario obligatorio España, RD-ley 8/2019, control horario empresas, registro jornada laboral obligatorio, sanciones registro horario, fichaje digital obligatorio, control horario pymes 2026" />
        <meta property="og:title" content="Registro Horario Obligatorio en España 2026: Guía Completa" />
        <meta property="og:description" content="Todo lo que necesitas saber sobre el registro horario obligatorio. Normativa, sanciones y cómo cumplir fácilmente." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://presenzia.app/blog/registro-horario-obligatorio-espana" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="PresenzIA" />
        <meta property="article:published_time" content="2026-04-05" />
        <meta property="article:section" content="Normativa Laboral" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://presenzia.app/blog/registro-horario-obligatorio-espana" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Registro Horario Obligatorio en España 2026: Guía Completa para Pymes",
          "description": "Guía completa sobre la obligación de registro horario en España según el RD-ley 8/2019. Sanciones, requisitos y soluciones digitales.",
          "author": { "@type": "Organization", "name": "PresenzIA", "url": "https://presenzia.app" },
          "publisher": { "@type": "Organization", "name": "PresenzIA", "url": "https://presenzia.app" },
          "datePublished": "2026-04-05",
          "dateModified": "2026-04-05",
          "mainEntityOfPage": "https://presenzia.app/blog/registro-horario-obligatorio-espana",
          "inLanguage": "es"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://presenzia.app/landing" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://presenzia.app/blog" },
            { "@type": "ListItem", "position": 3, "name": "Registro Horario Obligatorio en España 2026", "item": "https://presenzia.app/blog/registro-horario-obligatorio-espana" },
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "¿Qué empresas están obligadas al registro horario en España?", "acceptedAnswer": { "@type": "Answer", "text": "Todas las empresas con empleados por cuenta ajena en España, sin importar su tamaño o sector. La obligación aplica desde empresas de 1 empleado hasta grandes corporaciones." } },
            { "@type": "Question", "name": "¿Cuánto es la multa por no registrar el horario?", "acceptedAnswer": { "@type": "Answer", "text": "Las sanciones van desde 751 € (leve) hasta 187.515 € (muy grave). La cuantía depende de la gravedad, reincidencia y número de trabajadores afectados." } },
            { "@type": "Question", "name": "¿Se puede usar Excel para el registro horario?", "acceptedAnswer": { "@type": "Answer", "text": "Legalmente sí, pero no es recomendable. Excel no ofrece trazabilidad, es fácilmente manipulable y no permite demostrar la integridad de los registros ante una inspección. Un sistema digital con registro automatizado es mucho más seguro." } }
          ]
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Nav */}
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
            <Link to="/landing" className="flex items-center gap-2">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold text-gray-900">Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span></span>
            </Link>
            <Link
              to="/registro"
              onClick={() => trackBlogCta(SLUG, 'header_post')}
              className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              Prueba gratis <FiArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
          <Link to="/landing" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 transition-colors">
            <FiArrowLeft className="h-3.5 w-3.5" /> Volver a inicio
          </Link>
        </div>

        {/* Article */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-2 text-sm text-blue-600 font-medium mb-3">
              <FiCalendar className="h-4 w-4" />
              <span>Normativa Laboral</span>
              <span className="text-gray-300">·</span>
              <time dateTime="2026-04-05">5 de abril de 2026</time>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
              Registro Horario Obligatorio en España 2026: Guía Completa para Pymes
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              Todo lo que tu empresa necesita saber sobre la obligación de registrar la jornada laboral. Normativa vigente, sanciones, requisitos y cómo cumplir de forma sencilla y económica.
            </p>
          </header>

          {/* Table of contents */}
          <nav className="bg-gray-50 rounded-2xl p-6 mb-10 border border-gray-100">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Índice</h2>
            <ol className="space-y-2 text-sm text-blue-600">
              <li><a href="#que-dice-la-ley" className="hover:underline">1. ¿Qué dice la ley?</a></li>
              <li><a href="#quien-debe-cumplir" className="hover:underline">2. ¿Quién debe cumplir?</a></li>
              <li><a href="#sanciones" className="hover:underline">3. Sanciones por incumplimiento</a></li>
              <li><a href="#que-debe-registrarse" className="hover:underline">4. ¿Qué debe registrarse?</a></li>
              <li><a href="#metodos" className="hover:underline">5. Métodos de registro: papel, Excel o digital</a></li>
              <li><a href="#como-implementar" className="hover:underline">6. Cómo implementar el registro horario en tu pyme</a></li>
              <li><a href="#preguntas-frecuentes" className="hover:underline">7. Preguntas frecuentes</a></li>
            </ol>
          </nav>

          {/* Content */}
          <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-blue-600 prose-strong:text-gray-900">

            <section id="que-dice-la-ley" className="mb-12">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
                ¿Qué dice la ley?
              </h2>
              <p>
                El <strong>Real Decreto-ley 8/2019</strong>, de 8 de marzo, de medidas urgentes de protección social y de lucha contra la precariedad laboral en la jornada de trabajo, modificó el artículo 34 del Estatuto de los Trabajadores para incluir la obligación del <strong>registro diario de jornada</strong>.
              </p>
              <p>
                Desde el <strong>12 de mayo de 2019</strong>, todas las empresas deben garantizar el registro diario de la jornada, que deberá incluir el horario concreto de inicio y finalización de la jornada de trabajo de cada persona trabajadora. Este marco se completa con <Link to="/blog/real-decreto-2026-fichaje-digital">el nuevo Real Decreto de fichaje digital previsto para 2026</Link>, que añade requisitos técnicos de trazabilidad e interoperabilidad con la ITSS.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg my-6">
                <div className="flex items-start gap-3">
                  <FiAlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-800 mb-1">Importante</p>
                    <p className="text-amber-700 text-sm">Los registros deben conservarse durante <strong>4 años</strong> y estar a disposición de los trabajadores, representantes legales e Inspección de Trabajo.</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="quien-debe-cumplir" className="mb-12">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
                ¿Quién debe cumplir?
              </h2>
              <p>
                <strong>Todas las empresas</strong> con trabajadores por cuenta ajena están obligadas, independientemente de:
              </p>
              <ul className="space-y-2 my-4">
                <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span>Tamaño de la empresa (desde 1 empleado)</span></li>
                <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span>Sector de actividad</span></li>
                <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span>Tipo de jornada (completa o parcial)</span></li>
                <li className="flex items-start gap-2"><FiCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> <span>Tipo de contrato (temporal, indefinido, fijo-discontinuo)</span></li>
              </ul>
              <p>
                Las <strong>micropymes y pymes</strong> son las que más riesgo tienen, ya que suelen carecer de sistemas formalizados y son las más inspeccionadas proporcionalmente. Si tienes entre 1 y 50 empleados, <strong>esta ley te afecta directamente</strong>.
              </p>
            </section>

            <section id="sanciones" className="mb-12">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-red-100 text-red-600 rounded-lg flex items-center justify-center text-sm font-bold">3</span>
                Sanciones por incumplimiento
              </h2>
              <p>
                No cumplir con el registro horario se considera una <strong>infracción grave en materia de relaciones laborales</strong> según la LISOS (Ley sobre Infracciones y Sanciones en el Orden Social).
              </p>
              <div className="overflow-x-auto my-6">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-3 font-semibold text-gray-900">Grado</th>
                      <th className="text-left p-3 font-semibold text-gray-900">Sanción mínima</th>
                      <th className="text-left p-3 font-semibold text-gray-900">Sanción máxima</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr><td className="p-3">Grado mínimo</td><td className="p-3">751 €</td><td className="p-3">1.500 €</td></tr>
                    <tr><td className="p-3">Grado medio</td><td className="p-3">1.501 €</td><td className="p-3">3.750 €</td></tr>
                    <tr><td className="p-3">Grado máximo</td><td className="p-3">3.751 €</td><td className="p-3">7.500 €</td></tr>
                  </tbody>
                </table>
              </div>
              <p>
                En caso de <strong>reincidencia o afectación a múltiples trabajadores</strong>, la sanción puede alcanzar hasta <strong>187.515 €</strong>. Además, la falta de registro puede ser utilizada como indicio en reclamaciones de horas extra no pagadas. Tienes el desglose detallado en <Link to="/blog/sanciones-no-fichar-2026">la guía actualizada de sanciones por no fichar en 2026</Link>.
              </p>
            </section>

            <section id="que-debe-registrarse" className="mb-12">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">4</span>
                ¿Qué debe registrarse?
              </h2>
              <p>La ley exige como mínimo el registro de:</p>
              <ul className="space-y-2 my-4">
                <li className="flex items-start gap-2"><FiClock className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> <span><strong>Hora de inicio</strong> de la jornada</span></li>
                <li className="flex items-start gap-2"><FiClock className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" /> <span><strong>Hora de finalización</strong> de la jornada</span></li>
              </ul>
              <p>
                Aunque no es obligatorio por ley, es <strong>altamente recomendable</strong> registrar también las <strong>pausas</strong> (descanso, comida) para evitar disputas sobre el cómputo de horas efectivas y horas extra.
              </p>
            </section>

            <section id="metodos" className="mb-12">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">5</span>
                Métodos de registro: papel, Excel o digital
              </h2>
              <p>
                La ley no especifica el método de registro. Sin embargo, cada opción tiene ventajas y riesgos diferentes, y conviene revisar <Link to="/blog/como-elegir-sistema-fichaje">los criterios para elegir un sistema de fichaje</Link> antes de decidir:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
                <div className="border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2">Papel</h3>
                  <p className="text-sm text-gray-500 mb-3">Hojas de firma</p>
                  <ul className="text-sm space-y-1">
                    <li className="text-red-500">Fácil de perder</li>
                    <li className="text-red-500">Difícil de consultar</li>
                    <li className="text-red-500">Sin trazabilidad</li>
                    <li className="text-green-600">Coste cero</li>
                  </ul>
                </div>
                <div className="border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2">Excel</h3>
                  <p className="text-sm text-gray-500 mb-3">Hoja de cálculo</p>
                  <ul className="text-sm space-y-1">
                    <li className="text-red-500">Manipulable</li>
                    <li className="text-red-500">Sin automatización</li>
                    <li className="text-amber-500">Algo de trazabilidad</li>
                    <li className="text-green-600">Coste bajo</li>
                  </ul>
                </div>
                <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-5 ring-2 ring-blue-500">
                  <h3 className="font-bold text-blue-900 mb-2">Digital (SaaS)</h3>
                  <p className="text-sm text-blue-600 mb-3">Recomendado</p>
                  <ul className="text-sm space-y-1">
                    <li className="text-green-600">Automatizado</li>
                    <li className="text-green-600">Trazabilidad total</li>
                    <li className="text-green-600">Informes al instante</li>
                    <li className="text-green-600">Desde 1,50 €/empl/mes</li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="como-implementar" className="mb-12">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-green-100 text-green-600 rounded-lg flex items-center justify-center text-sm font-bold">6</span>
                Cómo implementar el registro horario en tu pyme
              </h2>
              <p>Implementar un sistema de registro horario digital es más fácil de lo que piensas. Con <strong>PresenzIA</strong>, el proceso completo toma menos de 5 minutos:</p>
              <ol className="space-y-4 my-6">
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <div>
                    <p className="font-semibold text-gray-900">Regístrate gratis</p>
                    <p className="text-sm text-gray-500">Sin tarjeta de crédito. Crea tu cuenta en 30 segundos.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <div>
                    <p className="font-semibold text-gray-900">Añade a tus empleados</p>
                    <p className="text-sm text-gray-500">Invítalos por email o crea sus cuentas manualmente.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <div>
                    <p className="font-semibold text-gray-900">Empezad a fichar</p>
                    <p className="text-sm text-gray-500">Desde el móvil, tablet u ordenador. Un clic y listo.</p>
                  </div>
                </li>
              </ol>
              <NewsletterInline slug={SLUG} variant="card" />

              <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-8 text-center my-8">
                <h3 className="text-2xl font-bold text-white mb-2">¿Listo para cumplir la ley?</h3>
                <p className="text-blue-100 mb-6">Prueba PresenzIA gratis 14 días. Sin tarjeta, sin compromiso.</p>
                <Link
                  to="/registro"
                  onClick={() => trackBlogCta(SLUG, 'cta_final_post')}
                  className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold py-3 px-8 rounded-xl text-base shadow-xl hover:bg-blue-50 transition-all"
                >
                  Empezar gratis <FiArrowRight className="h-4 w-4" />
                </Link>
                <p className="text-blue-200 text-xs mt-3">Desde 1,50 €/empleado/mes. Sin mínimos.</p>
              </div>
            </section>

            <section id="preguntas-frecuentes" className="mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">7</span>
                Preguntas frecuentes
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">¿Qué empresas están obligadas al registro horario en España?</h3>
                  <p className="text-gray-600 text-sm">Todas las empresas con empleados por cuenta ajena en España, sin importar su tamaño o sector. La obligación aplica desde empresas de 1 empleado hasta grandes corporaciones.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">¿Cuánto es la multa por no registrar el horario?</h3>
                  <p className="text-gray-600 text-sm">Las sanciones van desde 751 € (leve) hasta 187.515 € (muy grave). La cuantía depende de la gravedad, reincidencia y número de trabajadores afectados.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">¿Se puede usar Excel para el registro horario?</h3>
                  <p className="text-gray-600 text-sm">Legalmente sí, pero no es recomendable. Excel no ofrece trazabilidad, es fácilmente manipulable y no permite demostrar la integridad de los registros ante una inspección. Un sistema digital con registro automatizado es mucho más seguro.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">¿Cuánto tiempo hay que guardar los registros?</h3>
                  <p className="text-gray-600 text-sm">Los registros deben conservarse durante un mínimo de 4 años, según establece el artículo 34.9 del Estatuto de los Trabajadores.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">¿Los empleados en teletrabajo también deben fichar?</h3>
                  <p className="text-gray-600 text-sm">Sí. La obligación de registro aplica independientemente de dónde se realice el trabajo. Los empleados en teletrabajo también deben registrar su jornada. Con un sistema digital como PresenzIA, pueden fichar desde cualquier dispositivo.</p>
                </div>
              </div>
            </section>
          </div>
        </article>

        {/* Footer */}
        <footer className="bg-gray-50 border-t border-gray-100 py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <Link to="/landing" className="inline-flex items-center gap-2 mb-4">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-6 h-6 rounded" />
              <span className="text-sm font-bold text-gray-900">Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span></span>
            </Link>
            <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} PresenzIA. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>
    </>
  )
}

export default BlogRegistroHorario
